/**
 * Site details sheet lifecycle — whether the compact-screen details drawer is
 * still showing the page it was opened over.
 *
 * The drawer belongs to the dashboard layout, which outlives every subpage
 * routed under it, and the cards inside it navigate: alarms and maintenance
 * jump to their history pages, the rail jumps to the command log. Nothing
 * about a route change unmounts the drawer, so an open sheet would sit over
 * the page the tap just opened. The drawer therefore records the path it was
 * opened over and is closed as soon as the location moves off it — one rule
 * that covers every navigation source, including the back gesture.
 *
 * Closing is a reset, not a suspension: a later return to the same path opens
 * a fresh sheet only if the user asks for one.
 *
 * Contains:
 * - DETAILS_DRAWER_CLOSED — the closed state
 * - openDetailsDrawer     — open state anchored to the current path
 * - resolveDetailsDrawer  — stored state + current path → state to render
 *
 * Returns plain JS — no JSX.
 */

export const DETAILS_DRAWER_CLOSED = Object.freeze({ isOpen: false, openedAtPath: null });

/**
 * @param {string} pathname - location.pathname the sheet is opened over.
 * @returns {{isOpen: boolean, openedAtPath: string|null}} Open state anchored to that path.
 */
export function openDetailsDrawer(pathname) {
  return { isOpen: true, openedAtPath: pathname ?? null };
}

/**
 * Resolves the state the drawer should be rendered in for the current path.
 *
 * Returns the identical `DETAILS_DRAWER_CLOSED` reference for every closed
 * outcome, so a caller can compare by identity to decide whether its stored
 * state needs resetting — and feeding the result back in is a fixed point,
 * which is what keeps a closed sheet closed when the user navigates back to
 * the path it was opened over.
 *
 * @param {{isOpen: boolean, openedAtPath: string|null}} state - stored drawer state.
 * @param {string} pathname - current location.pathname.
 * @returns {{isOpen: boolean, openedAtPath: string|null}} State to render.
 */
export function resolveDetailsDrawer(state, pathname) {
  if (!state?.isOpen) return DETAILS_DRAWER_CLOSED;
  return state.openedAtPath === (pathname ?? null) ? state : DETAILS_DRAWER_CLOSED;
}
